import {
  ColDef,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IsGroupOpenByDefaultParams,
  MenuItemDef,
  ValueGetterParams,
  RowGroupOpenedEvent,
  SelectionChangedEvent,
  Theme
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MEMBER_SUBSET_OP_LABELS, MemberRow, MemberSubsetOp } from 'src/components/star-grid/utils/memberSubset';
import './styles/pnl-member-subset-grid.scss';

/** ⊞ / ⊟ boxes, as the subset editor draws them. Elements, not strings — AG Grid escapes strings. */
const SUBSET_GRID_ICONS = {
  // Empty spans DRAWN as boxed +/- in CSS. The ⊞/⊟ glyphs rendered tiny and inconsistently across
  // fonts; a bordered box with a rule through it is legible at any size. `icons` is one of the few
  // AG Grid hooks that really does take an HTML string (unlike cellRenderer, which escapes them).
  groupExpanded: () => '<span class="sg-subset-toggle sg-subset-toggle-open"></span>',
  groupContracted: () => '<span class="sg-subset-toggle sg-subset-toggle-closed"></span>'
};

/**
 * Compact, ruled and quiet — the subset editor people already use is a dense list, not a data grid,
 * so an airy default theme reads as a different tool.
 */
const subsetTheme = (theme: Theme): Theme =>
  theme.withParams({
    /*
     * Inherit the surrounding font rather than naming one. The dialog's title, search box and buttons
     * are Cloudscape (Amazon Ember); pinning the grid to the native system stack to chase the macOS
     * screenshot put two typefaces inside one modal, which read as the mismatch it was.
     */
    fontFamily: 'inherit',
    fontSize: 13,
    headerFontSize: 13,
    // The column heading is a label, not an emphasis — it is not bold in the editor being matched.
    headerFontWeight: 400,
    cellHorizontalPadding: 4,
    iconSize: 13,
    // A member tree goes deep, so the default indent pushes deep labels off-screen.
    rowGroupIndentSize: 14,
    // Air between rows: the list was legible but cramped at the default scale.
    rowVerticalPaddingScale: 1.4,
    wrapperBorder: false,
    headerColumnBorder: false,
    rowBorder: false,
    // Literal colours, chosen to match the editor being replicated: a blue-grey header band over a
    // white body. Tokens were either too light to read as a band or the wrong hue.
    // A lighter tint of the header band for the body, so the table reads as one surface.
    backgroundColor: '#f4f7fa',
    headerBackgroundColor: '#dfe5ec',
    headerTextColor: 'var(--color-text-body-default, #000716)',
    // No zebra striping: indentation is what carries structure here, and stripes fight it.
    oddRowBackgroundColor: 'transparent',
    rowHoverColor: 'var(--color-background-dropdown-item-hover, #f4f4f4)',
    // Selection is shown by the checkbox alone; tinting the row as well turned a long list into
    // bands of blue and buried the labels.
    selectedRowBackgroundColor: 'transparent',
    // The context menu is portaled out of this container, so CSS nested under the grid cannot reach
    // it; these theme params can, because AG Grid stamps its theme class onto popups too. Sized to
    // match the dense list the menu acts on.
    menuTextColor: 'var(--color-text-body-default, #000716)',
    menuBackgroundColor: 'var(--color-background-container-content, #ffffff)',
    menuBorder: { color: 'var(--color-border-divider-default, #d1d5db)', width: 1, style: 'solid' },
    menuSeparatorColor: 'var(--color-border-divider-default, #e9ebed)',
    menuShadow: { offsetX: 0, offsetY: 2, radius: 8, spread: 0, color: 'rgba(0, 7, 22, 0.18)' },
    borderRadius: 4
  });

/** Order the context menu offers the operations in — Keep variants, widest scope last. */
const MENU_OPS: readonly MemberSubsetOp[] = ['keep', 'keepChildren', 'keepDescendants', 'keepLeaves', 'leavesOnly'];

/**
 * A tick for the Keep family and a leaf for the leaves-only variant, as the desktop editor marks
 * them. HTML strings because AG Grid menu icons take markup (unlike cellRenderer, which escapes it).
 */
const MENU_ICONS: Readonly<Record<MemberSubsetOp, string>> = {
  keep: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepChildren: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepDescendants: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepLeaves: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  leavesOnly: '<span class="sg-subset-menu-icon sg-subset-menu-leaf">\u2740</span>'
};

const REMOVE_ICON = '<span class="sg-subset-menu-icon sg-subset-menu-remove">\u2715</span>';

export interface PnlMemberSubsetGridProps {
  readonly rows: readonly MemberRow[];
  readonly theme: Theme;
  /** Position keys currently ticked. */
  readonly checked: ReadonlySet<string>;
  /** Receives the whole checked set: ticking a parent cascades to its descendants. */
  readonly onCheckedChanged: (keys: readonly string[]) => void;
  /** Reports the grid's expansion back, so the caller can derive what is visible. */
  readonly onExpandedChanged: (key: string, expanded: boolean) => void;
  /** Which rows start open — the operation decides this, not the previous browsing state. */
  readonly initiallyExpanded: ReadonlySet<string>;
  /** Rows to tick when the grid first renders, restoring a previously confirmed selection. */
  readonly initiallyChecked: ReadonlySet<string>;
  readonly onRunOp: (op: MemberSubsetOp, selected: readonly string[]) => void;
  readonly onRemove: (selected: readonly string[], withDescendants: boolean) => void;
  readonly testId?: string;
}

/**
 * The member tree as an AG Grid, for VIRTUALIZATION: only the rows in view reach the DOM, so a
 * `Keep Leaves` over a few thousand cost centres renders as cheaply as a handful.
 *
 * Tree shape comes from `displayPath` — a chain of ROW KEYS, not member ids. Two reasons: keys
 * cannot collide, so a member sitting under several rollups stays one row per rollup rather than the
 * rows merging; and the path encodes the levels the operation chose, so `Keep + Leaves` keeps its
 * intermediate consolidations collapsed away instead of AG Grid resurrecting them as placeholder
 * group rows (which is what true hierarchy paths would do).
 *
 * AG Grid is handed the operation's FULL row set and owns expand/collapse; the caller mirrors that
 * state through `onExpandedChanged` and derives membership from it. Handing over only the visible
 * rows instead looked tidier but broke re-expansion: collapsing a node removed its children from the
 * data, so the grid dropped the expander and the node could never be opened again. Virtualization
 * means the full set costs nothing — only rows in view reach the DOM.
 */
const PnlMemberSubsetGrid: React.FC<PnlMemberSubsetGridProps> = ({
  rows,
  theme,
  checked,
  onCheckedChanged,
  onExpandedChanged,
  initiallyExpanded,
  initiallyChecked,
  onRunOp,
  onRemove,
  testId
}) => {
  /*
   * The context menu renders INSIDE this container rather than at the document body, which is the
   * only way stylesheet rules can reach it — AG Grid stamps a generated theme class on body-level
   * popups that static CSS cannot name. The grid keeps the menu within these bounds, so a tall menu
   * flips rather than overflowing.
   */
  const [popupParent, setPopupParent] = useState<HTMLDivElement | null>(null);

  const getDataPath = useCallback((row: MemberRow): string[] => [...row.displayPath], []);
  const getRowId = useCallback(({ data }: { data: MemberRow }): string => data.key, []);

  // No leading columns: the tree column is the whole grid. A row-number gutter was noise next to the
  // names, and its numbers were positional, so they needed repainting on every expand.
  const columnDefs = useMemo<ColDef<MemberRow>[]>(() => [], []);

  const autoGroupColumnDef = useMemo<ColDef<MemberRow>>(
    () => ({
      headerName: 'Name',
      flex: 1,
      minWidth: 240,
      // Centred, as the subset editor heads its single column.
      headerClass: 'sg-subset-name-header',
      // The path is row keys, so the label has to come off the row rather than the group key.
      valueGetter: (params: ValueGetterParams<MemberRow>) => params.data?.label ?? '',
      cellRendererParams: { suppressCount: true },
      cellClass: (params: { data?: MemberRow }) => (params.data?.isLeaf ? 'sg-subset-leaf' : 'sg-subset-node')
    }),
    []
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<MemberRow>): (MenuItemDef | DefaultMenuItem)[] => {
      const clicked = params.node?.data;
      if (!clicked) return [];
      // Operate on the ticked rows when the clicked row is one of them, else on the clicked row —
      // the familiar file-manager rule.
      const selected = checked.has(clicked.key) ? [...checked] : [clicked.key];
      return [
        // AG Grid's own clipboard item, so Copy behaves like it does everywhere else in the app.
        'copy',
        'separator',
        ...MENU_OPS.map((op) => ({
          name: MEMBER_SUBSET_OP_LABELS[op],
          icon: MENU_ICONS[op],
          action: () => onRunOp(op, selected)
        })),
        'separator',
        {
          name: 'Remove Elements',
          icon: REMOVE_ICON,
          /*
           * Follows what is on screen: a COLLAPSED node takes its descendants with it, because they
           * are not visible and leaving them behind would make hidden members reappear a level up.
           * An EXPANDED node is removed alone, and its children lift a level (see applyExclusions) —
           * you can see them, so removing them was not what you asked for.
           *
           * Decided per row, since a multi-row selection can mix the two states. Leaves report as
           * collapsed, where the distinction is moot anyway.
           */
          action: () => {
            const collapsed = selected.filter((key) => params.api.getRowNode(key)?.expanded !== true);
            const opened = selected.filter((key) => params.api.getRowNode(key)?.expanded === true);
            if (collapsed.length > 0) onRemove(collapsed, true);
            if (opened.length > 0) onRemove(opened, false);
          }
        },
        {
          name: 'Remove Elements + Descendants',
          icon: REMOVE_ICON,
          // Explicit, so an expanded branch can still be removed whole.
          action: () => onRemove(selected, true)
        }
      ];
    },
    [checked, onRunOp, onRemove]
  );

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<MemberRow>) => {
      const key = event.data?.key;
      if (key) onExpandedChanged(key, event.node.expanded === true);
    },
    [onExpandedChanged]
  );

  // Seeds the grid's own expansion from the operation. Deliberately never pushed back into the grid
  // afterwards: mirroring state in both directions is how expand/collapse loops get built.
  const isGroupOpenByDefault = useCallback(
    (params: IsGroupOpenByDefaultParams<MemberRow>): boolean => initiallyExpanded.has(params.rowNode.data?.key ?? ''),
    [initiallyExpanded]
  );

  /*
   * Seed the grid's selection ONCE per mount, so reopening the editor shows what was confirmed last
   * time. A ref guard rather than an effect dependency: re-applying it on later model updates would
   * undo the user's own deselections, which is the two-way-sync trap that expansion already avoids.
   */
  const seededRef = useRef(false);
  const seedSelection = useCallback(
    (api: GridApi<MemberRow>) => {
      if (seededRef.current || initiallyChecked.size === 0) return;
      seededRef.current = true;
      const nodes: IRowNode<MemberRow>[] = [];
      api.forEachNode((node) => {
        if (node.data && initiallyChecked.has(node.data.key)) nodes.push(node);
      });
      if (nodes.length > 0) api.setNodesSelected({ nodes, newValue: true });
    },
    [initiallyChecked]
  );

  const onFirstDataRendered = useCallback((event: FirstDataRenderedEvent<MemberRow>) => seedSelection(event.api), [seedSelection]);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<MemberRow>) => {
      const keys = event.api
        .getSelectedNodes()
        .map((node) => node.data?.key ?? '')
        .filter(Boolean);
      onCheckedChanged(keys);
    },
    [onCheckedChanged]
  );

  return (
    <div className="sg-subset-grid" ref={setPopupParent} data-testid={testId}>
      <AgGridReact<MemberRow>
        rowData={rows as MemberRow[]}
        getRowId={getRowId}
        columnDefs={columnDefs}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData
        getDataPath={getDataPath}
        isGroupOpenByDefault={isGroupOpenByDefault}
        /*
         * Checkboxes, cascading down: ticking a rollup ticks everything under it, which is how the
         * old picker behaved and what makes selecting a branch one click instead of hundreds.
         * `enableClickSelection` stays off so clicking a label cannot silently re-select a whole
         * subtree.
         */
        rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: false, groupSelects: 'descendants', enableClickSelection: false }}
        onSelectionChanged={onSelectionChanged}
        onFirstDataRendered={onFirstDataRendered}
        onRowGroupOpened={onRowGroupOpened}
        icons={SUBSET_GRID_ICONS}
        getContextMenuItems={getContextMenuItems}
        rowHeight={26}
        headerHeight={26}
        theme={subsetTheme(theme)}
        popupParent={popupParent ?? undefined}
        suppressCellFocus
      />
    </div>
  );
};

export default PnlMemberSubsetGrid;
